import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type Ref,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Customized,
  Line,
  type PlotArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ZIndexLayer,
  usePlotArea,
} from 'recharts'
import { cn, formatNumber } from '../../lib/utils'
import type {
  BandedTimelineChartProps,
  BarLayout,
  TimelineBand,
  TimelineBarSegment,
  TimelineCardContent,
  TimelineCardRow,
  TimelineMarker,
  TimelineSeries,
} from './banded-timeline-types'

const DEFAULT_MIN_SLOT_WIDTH = 6
const DEFAULT_BAR_WIDTH_RATIO = 0.6
// Historic back-office height: a caller passing no height keeps exactly the former rendering.
const DEFAULT_CHART_HEIGHT = 362
// Has to fit the hour labels and the marker row above the bars (XAxis orientation="top").
const CHART_TOP_MARGIN = 12
const CHART_MARGIN = { top: CHART_TOP_MARGIN, right: 0, bottom: 0, left: 0 }
// Pinned rather than left to the recharts default: the Y tick labels are placed from it.
const X_AXIS_HEIGHT = 30
const PLOT_TOP = CHART_TOP_MARGIN + X_AXIS_HEIGHT
const plotHeightOf = (chartHeight: number): number => chartHeight - PLOT_TOP - CHART_MARGIN.bottom

// The height crosses deeply nested parts (bands, hover cards, Y axis), hence a context.
const ChartHeightContext = createContext(DEFAULT_CHART_HEIGHT)
const useChartHeight = (): number => useContext(ChartHeightContext)
const usePlotHeight = (): number => plotHeightOf(useChartHeight())
const HEADER_ROW_HEIGHT = 28
// The circle behind a marker, wider than the glyph its consumer draws inside.
const MARKER_SLOT_SIZE = 26
// The row exists only to sit that circle on top of the plot, so it follows it.
const MARKER_ROW_HEIGHT = MARKER_SLOT_SIZE
// A marker that drops a line onto the bars sits on its own row, clear of the ones that stay up top.
const DROP_LINE_MARKER_OFFSET = 45
const MIN_TICK_GAP_PX = 40
const MIN_Y_TICK_GAP_PX = 14
const LINE_STROKE_WIDTH = 1.5
// Ticks up there would collide with the markers that sit inside the plot, and the lowest one
// would hang half outside the band.
const AXIS_LABEL_HALF_HEIGHT = 8
// Bends the segment under the midpoint so values near the floor read: at 9 a value halfway up
// that segment sits at 74 % of its height instead of 50 %.
const LOWER_SEGMENT_BEND = 9
const GRID_LINE_COLOR = '#152013'
const GRID_LINE_OPACITY = 0.08
const GRID_DASH = '1 3'
const Y_AXIS_LABEL_INSET = 16
const PROJECTED_DOMAIN: [number, number] = [0, 1]
const BAND_GAP_PX = 4
// Below this the chart stops filling the row and scrolls instead, rather than collapsing.
const SLOT_WIDTH_FLOOR = 5
// Narrower than this, day badges move under the day label instead of beside it.
const HEADER_EXTRAS_MIN_WIDTH = 260
const HEADER_BADGE_ROW_HEIGHT = 20
// The ratio alone would leave bars touching once slots get narrow, so the gutter has a floor.
const MIN_BAR_GUTTER_PX = 1.5

function barSizeFor(slotWidth: number, ratio: number): number {
  const gutter = Math.max(MIN_BAR_GUTTER_PX, slotWidth * (1 - ratio))
  return Math.max(1, slotWidth - gutter)
}
const NOW_LINE_COLOR = '#94a3b8'
const DIM_OPACITY = 0.5
// Without a z-index it paints before bar/line/axis (300/400/500): invisible and unhoverable.
const MARKER_Z_INDEX = 550

const CARD_BACKGROUND = '#f9f8f1'
const CARD_RADIUS = 5
const CARD_PADDING = 10
const CARD_GAP = 8
const CARD_SEPARATOR_COLOR = '#b3c8ad'
const CARD_TEXT_COLOR = '#152013'
const CARD_LABEL_COLOR = '#68885f'
const CARD_SHADOW = 'drop-shadow(0 2px 6px rgba(21, 32, 19, 0.12))'
const CARD_ARROW_DEPTH = 7.72
const CARD_ARROW_SPAN = 13.2
const CARD_ARROW_PATH =
  'M4.89461 6.87803L0 0H13.1963L8.13709 6.90092C7.33099 8.00048 5.6851 7.98886 4.89461 6.87803Z'
const CARD_ARROW_TRANSFORMS: Record<ArrowSide, string> = {
  left: 'translate(7.71842 0) rotate(90)',
  right: 'translate(0 13.1963) rotate(270)',
  top: 'translate(13.1963 7.71842) rotate(180)',
}
const CARD_EDGE_GAP = 4
const CARD_MIN_WIDTH = 122
const CARD_MAX_WIDTH = 260
const MARKER_CLEARANCE = MARKER_SLOT_SIZE / 2 + CARD_ARROW_DEPTH

interface TimelineRecord {
  t: string
  epoch: number
  values: Record<string, number | null>
}

function seedValues(series: TimelineSeries[]): Record<string, number | null> {
  const values: Record<string, number | null> = {}
  for (const currentSeries of series) {
    values[currentSeries.key] = null
  }
  return values
}

function buildRecords(series: TimelineSeries[]): TimelineRecord[] {
  const byTimestamp = new Map<string, Record<string, number | null>>()
  for (const currentSeries of series) {
    for (const point of currentSeries.data) {
      const values = byTimestamp.get(point.t) ?? seedValues(series)
      values[currentSeries.key] = point.value
      byTimestamp.set(point.t, values)
    }
  }
  return [...byTimestamp.entries()]
    .map(([t, values]) => ({ t, epoch: new Date(t).getTime(), values }))
    .sort((a, b) => a.epoch - b.epoch)
}

function computeYDomain(series: TimelineSeries[]): [number, number] {
  let min = 0
  let max = 0
  for (const currentSeries of series) {
    if (currentSeries.outsideYDomain) continue
    for (const point of currentSeries.data) {
      if (point.value === null) continue
      if (point.value < min) min = point.value
      if (point.value > max) max = point.value
    }
  }
  return max > min ? [min, max] : [min, min + 1]
}

interface YProjection {
  domain: [number, number]
  ratio: (value: number) => number
  ticks: number[]
}

// Rounds around the requested count, where intraday-bars-chart's getNiceTickStep undershoots it.
function niceStep(span: number, intervals: number): number {
  if (span <= 0) return 1
  const rough = span / Math.max(1, intervals)
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalized = rough / magnitude
  if (normalized <= 1.5) return magnitude
  if (normalized <= 3) return 2 * magnitude
  if (normalized <= 7) return 5 * magnitude
  return 10 * magnitude
}

function roundValuesBetween(from: number, to: number, intervals: number): number[] {
  const step = niceStep(to - from, intervals)
  const values: number[] = []
  for (let value = Math.ceil(from / step) * step; value < to; value += step) {
    if (value > from) values.push(Number(value.toFixed(6)))
  }
  return values
}

// Bounds are always labelled; a round value joins them only where it stays legible and,
// since labels are rounded, where it does not read as one already on the axis.
function spacedTicks(
  bounds: number[],
  candidates: number[],
  ratio: (value: number) => number,
  plotHeight: number,
): number[] {
  const kept = [...bounds]
  const labels = new Set(bounds.map(formatNumber))
  for (const candidate of candidates) {
    const label = formatNumber(candidate)
    if (labels.has(label)) continue
    const crowded = kept.some(
      (value) => Math.abs(ratio(value) - ratio(candidate)) * plotHeight < MIN_Y_TICK_GAP_PX,
    )
    if (crowded) continue
    labels.add(label)
    kept.push(candidate)
  }
  return kept.sort((left, right) => left - right)
}

function createProjection(
  domain: [number, number],
  midpoint: number | undefined,
  plotHeight: number,
): YProjection {
  const [min, max] = domain
  const middle = midpoint !== undefined && midpoint > min && midpoint < max ? midpoint : null
  if (middle === null) {
    const span = max - min || 1
    const ratio = (value: number) => (value - min) / span
    return {
      domain,
      ratio,
      ticks: spacedTicks([min, max], roundValuesBetween(min, max, 12), ratio, plotHeight),
    }
  }
  const lowerRatio = (value: number) => {
    const share = (value - min) / (middle - min)
    return 0.5 * (Math.log1p(LOWER_SEGMENT_BEND * share) / Math.log1p(LOWER_SEGMENT_BEND))
  }
  const ratio = (value: number) =>
    value <= middle ? lowerRatio(value) : 0.5 + (0.5 * (value - middle)) / (max - middle)
  const candidates = [...roundValuesBetween(min, middle, 6), ...roundValuesBetween(middle, max, 6)]
  return { domain, ratio, ticks: spacedTicks([min, middle, max], candidates, ratio, plotHeight) }
}

// A negative gap centres each following bar on the first one, so equal widths overlap exactly.
function computeBarGap(
  series: TimelineSeries[],
  minSlotWidth: number,
  barLayout: BarLayout,
): number {
  if (barLayout !== 'overlap') return 0
  const barSeries = series.filter((currentSeries) => currentSeries.type === 'bar')
  if (barSeries.length < 2) return 0
  const [first, second] = barSeries
  const firstSize = barSizeFor(minSlotWidth, first.barWidthRatio ?? DEFAULT_BAR_WIDTH_RATIO)
  const secondSize = barSizeFor(minSlotWidth, second.barWidthRatio ?? DEFAULT_BAR_WIDTH_RATIO)
  return -(firstSize + secondSize) / 2
}

function thinTicks(
  records: TimelineRecord[],
  tickFilter: (t: string) => boolean,
  slotWidth: number,
): string[] {
  const indexes = records.reduce<number[]>((kept, record, index) => {
    if (tickFilter(record.t)) kept.push(index)
    return kept
  }, [])
  if (indexes.length < 2) return indexes.map((index) => records[index].t)

  const spacingPx = (indexes[1] - indexes[0]) * slotWidth
  const keepEvery = Math.max(1, Math.ceil(MIN_TICK_GAP_PX / Math.max(spacingPx, 1)))
  return indexes
    .filter((_, position) => position % keepEvery === 0)
    .map((index) => records[index].t)
}

function useMeasuredWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, width]
}

// Mirrors recharts (combineAllBarPositions.js) with the real barGap, otherwise the slot is mis-sized.
function computeSlotWidth(series: TimelineSeries[], minSlotWidth: number, barGap: number): number {
  const barSeries = series.filter((currentSeries) => currentSeries.type === 'bar')
  if (barSeries.length === 0) return minSlotWidth
  const sizes = barSeries.map((currentSeries) =>
    barSizeFor(minSlotWidth, currentSeries.barWidthRatio ?? DEFAULT_BAR_WIDTH_RATIO),
  )
  const sum = sizes.reduce((total, size) => total + size, 0) + (barSeries.length - 1) * barGap
  return Math.max(minSlotWidth, sum)
}

function bandBounds(band: TimelineBand): [number, number] {
  return [new Date(band.from).getTime(), new Date(band.to).getTime()]
}

function isWithinBand(t: string, band: TimelineBand): boolean {
  const epoch = new Date(t).getTime()
  const [from, to] = bandBounds(band)
  return epoch >= from && epoch < to
}

// Interpolates an index over time, never the hour; null outside the range to fall back to the band.
function locatePosition(records: TimelineRecord[], t: string): number | null {
  if (records.length === 0) return null
  const epoch = new Date(t).getTime()
  const lastIndex = records.length - 1
  if (epoch < records[0].epoch || epoch > records[lastIndex].epoch) return null
  for (let index = 0; index < lastIndex; index++) {
    const current = records[index]
    const next = records[index + 1]
    if (epoch >= current.epoch && epoch <= next.epoch) {
      const span = next.epoch - current.epoch
      return span === 0 ? index : index + (epoch - current.epoch) / span
    }
  }
  return lastIndex
}

function bandTimeRatio(t: string, band: TimelineBand): number {
  const start = new Date(band.from).getTime()
  const end = new Date(band.to).getTime()
  const span = end - start
  if (span <= 0) return 0
  const epoch = new Date(t).getTime()
  return Math.min(1, Math.max(0, (epoch - start) / span))
}

// Recharts draws the bar at the start of its slot and leaves the gutter on the right, so the
// bar centre sits half a bar width in, not half a slot. Without bars it switches to a point
// scale, where the centring formula differs.
function xFor(
  position: number,
  pointCount: number,
  hasBarSeries: boolean,
  plotArea: PlotArea,
  barWidthRatio?: number,
) {
  if (hasBarSeries) {
    const slot = plotArea.width / pointCount
    const centre = barWidthRatio ? barSizeFor(slot, barWidthRatio) / 2 : slot / 2
    return plotArea.x + position * slot + centre
  }
  const step = plotArea.width / Math.max(1, pointCount - 1)
  const start = (plotArea.width - step * (pointCount - 1)) / 2
  return plotArea.x + start + position * step
}

// A marker belongs to a slot and sits on its bar; an instant falls inside the slot, at its own
// fraction of it. Centring the latter would push it half a slot late.
function instantRatio(t: string, band: TimelineBand, records: TimelineRecord[]): number {
  const position = locatePosition(records, t)
  if (position === null) return bandTimeRatio(t, band)
  return position / Math.max(1, records.length)
}

function instantX(
  t: string,
  band: TimelineBand,
  records: TimelineRecord[],
  plotArea: PlotArea,
): number {
  return plotArea.x + instantRatio(t, band, records) * plotArea.width
}

function resolveX(
  t: string,
  band: TimelineBand,
  records: TimelineRecord[],
  hasBarSeries: boolean,
  plotArea: PlotArea,
  barWidthRatio?: number,
): number {
  const position = locatePosition(records, t)
  if (position !== null)
    return xFor(position, records.length, hasBarSeries, plotArea, barWidthRatio)
  return plotArea.x + bandTimeRatio(t, band) * plotArea.width
}

function topRoundedPath(x: number, y: number, width: number, height: number, radius: number) {
  const r = Math.max(0, Math.min(radius, height, width / 2))
  return [
    `M${x},${y + height}`,
    `L${x},${y + r}`,
    `A${r},${r} 0 0 1 ${x + r},${y}`,
    `L${x + width - r},${y}`,
    `A${r},${r} 0 0 1 ${x + width},${y + r}`,
    `L${x + width},${y + height}`,
    'Z',
  ].join('')
}

interface PositionedSegment {
  segment: TimelineBarSegment
  epoch: number
  path: string
  top: number
}

// Caps the top of its bar, so the loss reads as a share of that hour's column rather than a
// separate series added beside it.
function positionSegments(
  segments: TimelineBarSegment[],
  records: TimelineRecord[],
  projection: YProjection,
  plotArea: PlotArea,
  barWidthRatio?: number,
): PositionedSegment[] {
  const [domainMin] = projection.domain
  const yFor = (value: number) => plotArea.y + plotArea.height * (1 - projection.ratio(value))
  const slot = plotArea.width / Math.max(1, records.length)
  const width = barWidthRatio ? barSizeFor(slot, barWidthRatio) : slot

  return segments.flatMap((segment) => {
    const epoch = new Date(segment.t).getTime()
    const index = records.findIndex((record) => record.epoch === epoch)
    if (index === -1 || segment.value <= 0) return []
    const base = records[index].values[segment.seriesKey]
    if (base === null || base === undefined || base <= domainMin) return []
    const top = yFor(base)
    const bottom = yFor(Math.max(domainMin, base - segment.value))
    return [
      {
        epoch,
        path: topRoundedPath(
          plotArea.x + index * slot,
          top,
          width,
          bottom - top,
          segment.topRadius ?? 0,
        ),
        segment,
        top,
      },
    ]
  })
}

interface HoveredMarker {
  marker: TimelineMarker
  x: number
  y: number
}

const MARKER_GLYPH_STYLE: CSSProperties = {
  display: 'flex',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
}

const MARKER_BUTTON_STYLE: CSSProperties = {
  ...MARKER_GLYPH_STYLE,
  padding: 0,
  border: 'none',
  background: 'none',
}

function markerGlyphStyle(marker: TimelineMarker, base: CSSProperties): CSSProperties {
  return {
    ...base,
    color: marker.filled ? '#ffffff' : marker.color,
    cursor: marker.onClick ? 'pointer' : 'help',
  }
}

const MarkerGlyph = ({
  marker,
  onHover,
}: {
  marker: TimelineMarker
  onHover: (hovered: boolean) => void
}) => {
  const handleClick = marker.onClick
  const handlers = {
    onMouseEnter: () => onHover(true),
    onMouseLeave: () => onHover(false),
  }
  if (!handleClick) {
    return (
      <div {...handlers} style={markerGlyphStyle(marker, MARKER_GLYPH_STYLE)}>
        {marker.icon}
      </div>
    )
  }
  return (
    <button
      type="button"
      aria-label={marker.ariaLabel}
      onClick={handleClick}
      {...handlers}
      style={markerGlyphStyle(marker, MARKER_BUTTON_STYLE)}
    >
      {marker.icon}
    </button>
  )
}

interface HoveredSlot {
  t: string
  x: number
  y: number
  values: Record<string, number | null>
}

interface BandOverlayProps {
  band: TimelineBand
  records: TimelineRecord[]
  markers: TimelineMarker[]
  segments: TimelineBarSegment[]
  projection: YProjection
  nowLine: string | null
  hasBarSeries: boolean
  barWidthRatio?: number
  onSlotHover: (hovered: HoveredSlot | null) => void
  onMarkerHover: (hovered: HoveredMarker | null) => void
}

const BandOverlay = ({
  band,
  records,
  markers,
  segments,
  projection,
  nowLine,
  hasBarSeries,
  barWidthRatio,
  onSlotHover,
  onMarkerHover,
}: BandOverlayProps) => {
  const chartHeight = useChartHeight()
  const plotArea = usePlotArea()
  if (!plotArea) return null

  const positionedSegments = positionSegments(
    segments,
    records,
    projection,
    plotArea,
    barWidthRatio,
  )
  // A marker whose slot is capped points at that cap, not at the axis below it.
  const segmentTopByEpoch = new Map(
    positionedSegments.map((positioned) => [positioned.epoch, positioned.top]),
  )
  const markerRowY = plotArea.y - MARKER_ROW_HEIGHT / 2
  const positioned = markers.map((marker) => ({
    marker,
    x: resolveX(marker.t, band, records, hasBarSeries, plotArea, barWidthRatio),
    y: markerRowY + (marker.dropLine ? DROP_LINE_MARKER_OFFSET : 0),
  }))
  const nowX = nowLine ? instantX(nowLine, band, records, plotArea) : null

  const handleMouseMove = (event: MouseEvent<SVGRectElement>): void => {
    if (records.length === 0) return
    const bounds = event.currentTarget.getBoundingClientRect()
    const mouseX = plotArea.x + ((event.clientX - bounds.left) / bounds.width) * plotArea.width
    let bestIndex = 0
    let bestDistance = Number.POSITIVE_INFINITY
    for (let index = 0; index < records.length; index++) {
      const distance = Math.abs(
        mouseX - xFor(index, records.length, hasBarSeries, plotArea, barWidthRatio),
      )
      if (distance < bestDistance) {
        bestDistance = distance
        bestIndex = index
      }
    }
    const record = records[bestIndex]
    if (!record) return
    onSlotHover({
      t: record.t,
      x: xFor(bestIndex, records.length, hasBarSeries, plotArea, barWidthRatio),
      y: plotArea.y + plotArea.height / 2,
      values: record.values,
    })
  }

  return (
    <ZIndexLayer zIndex={MARKER_Z_INDEX}>
      <g>
        {records.length > 0 ? (
          <rect
            x={plotArea.x}
            y={plotArea.y}
            width={plotArea.width}
            height={plotArea.height}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => onSlotHover(null)}
          />
        ) : null}
        {nowX !== null ? (
          <line
            x1={nowX}
            x2={nowX}
            y1={0}
            y2={chartHeight}
            stroke={NOW_LINE_COLOR}
            strokeDasharray="3 3"
            strokeWidth={1.5}
            pointerEvents="none"
          />
        ) : null}
        {positionedSegments.map(({ segment, path }) => (
          <path
            key={`segment-${segment.seriesKey}-${segment.t}`}
            d={path}
            fill={segment.color}
            pointerEvents="none"
          />
        ))}
        {positioned.map(({ marker, x, y }) =>
          marker.dropLine ? (
            <line
              key={`drop-${marker.id}`}
              x1={x}
              x2={x}
              y1={y + MARKER_SLOT_SIZE / 2}
              y2={Math.max(
                segmentTopByEpoch.get(new Date(marker.t).getTime()) ?? plotArea.y + plotArea.height,
                y + MARKER_SLOT_SIZE / 2,
              )}
              stroke={marker.color}
              strokeWidth={1}
              opacity={0.7}
              pointerEvents="none"
            />
          ) : null,
        )}
        {positioned.map(({ marker, x, y }) => (
          <g key={marker.id}>
            {marker.filled ? (
              <circle
                cx={x}
                cy={y}
                r={MARKER_SLOT_SIZE / 2}
                fill={marker.color}
                pointerEvents="none"
              />
            ) : null}
            <foreignObject
              x={x - MARKER_SLOT_SIZE / 2}
              y={y - MARKER_SLOT_SIZE / 2}
              width={MARKER_SLOT_SIZE}
              height={MARKER_SLOT_SIZE}
            >
              <MarkerGlyph
                marker={marker}
                onHover={(hovered) => onMarkerHover(hovered ? { marker, x, y } : null)}
              />
            </foreignObject>
          </g>
        ))}
      </g>
    </ZIndexLayer>
  )
}

type ArrowSide = 'left' | 'right' | 'top'

interface CardPlacement {
  left: number
  top: number
  arrowSide: ArrowSide
  arrowOffset: number
}

interface CardAnchor {
  x: number
  y: number
  clearance: number
}

// How far the card may reach past its own band before hitting the edge of the scroller.
interface CardSpill {
  left: number
  right: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

// Narrow bands leave no room beside the anchor, so the card drops below it with a top arrow.
// A nine-day band is narrower than the card itself, hence the spill onto its neighbours.
function placeCard(
  anchor: CardAnchor,
  cardWidth: number,
  cardHeight: number,
  bandWidth: number,
  spill: CardSpill,
  chartHeight: number,
): CardPlacement {
  const minLeft = CARD_EDGE_GAP - spill.left
  const maxLeft = bandWidth + spill.right - cardWidth - CARD_EDGE_GAP
  const sideTop = clamp(anchor.y - cardHeight / 2, 0, chartHeight - cardHeight)

  if (anchor.x + anchor.clearance <= maxLeft) {
    return {
      left: anchor.x + anchor.clearance,
      top: sideTop,
      arrowSide: 'left',
      arrowOffset: anchor.y - sideTop,
    }
  }
  if (anchor.x - anchor.clearance - cardWidth >= minLeft) {
    return {
      left: anchor.x - anchor.clearance - cardWidth,
      top: sideTop,
      arrowSide: 'right',
      arrowOffset: anchor.y - sideTop,
    }
  }
  const left = clamp(anchor.x - cardWidth / 2, minLeft, maxLeft)
  return {
    left,
    top: anchor.y + anchor.clearance,
    arrowSide: 'top',
    arrowOffset: clamp(anchor.x - left, CARD_RADIUS, cardWidth - CARD_RADIUS),
  }
}

function arrowStyle(side: ArrowSide, offset: number | string): CSSProperties {
  if (side === 'top') {
    return { top: -CARD_ARROW_DEPTH, left: offset, transform: 'translateX(-50%)' }
  }
  const edge = side === 'left' ? { left: -CARD_ARROW_DEPTH } : { right: -CARD_ARROW_DEPTH }
  return { top: offset, ...edge, transform: 'translateY(-50%)' }
}

const CardArrow = ({ side, offset }: { side: ArrowSide; offset: number | string }) => {
  const vertical = side === 'top'
  return (
    <svg
      className="absolute"
      width={vertical ? CARD_ARROW_SPAN : CARD_ARROW_DEPTH}
      height={vertical ? CARD_ARROW_DEPTH : CARD_ARROW_SPAN}
      viewBox={vertical ? '0 0 13.1963 7.71842' : '0 0 7.71842 13.1963'}
      fill="none"
      style={arrowStyle(side, offset)}
    >
      <path d={CARD_ARROW_PATH} transform={CARD_ARROW_TRANSFORMS[side]} fill={CARD_BACKGROUND} />
    </svg>
  )
}

const CardBody = ({
  ref,
  minWidth,
  maxWidth,
  title,
  rows,
}: {
  ref?: Ref<HTMLDivElement>
  minWidth?: number
  maxWidth?: number
  title: ReactNode
  rows: TimelineCardRow[]
}) => (
  <div
    ref={ref}
    className="flex w-max shrink-0 flex-col"
    style={{
      minWidth,
      maxWidth,
      gap: CARD_GAP,
      padding: CARD_PADDING,
      borderRadius: CARD_RADIUS,
      background: CARD_BACKGROUND,
    }}
  >
    <div
      className="flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-5"
      style={{ color: CARD_TEXT_COLOR }}
    >
      {title}
    </div>
    {rows.length > 0 ? (
      <>
        <span style={{ height: 1, background: CARD_SEPARATOR_COLOR }} />
        <div className="flex flex-col text-xs uppercase">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-2">
              <span className="min-w-0 shrink font-medium" style={{ color: CARD_LABEL_COLOR }}>
                {row.label}
              </span>
              <span className="min-w-0 shrink-0 font-semibold" style={{ color: CARD_TEXT_COLOR }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </>
    ) : null}
  </div>
)

const HoverCard = ({
  anchor,
  bandWidth,
  spill,
  topOffset,
  title,
  rows,
}: {
  anchor: CardAnchor
  bandWidth: number
  spill: CardSpill
  topOffset: number
  title: ReactNode
  rows: TimelineCardRow[]
}) => {
  const chartHeight = useChartHeight()
  const cardRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ width: 0, height: 0 })
  const maxWidth = Math.min(
    CARD_MAX_WIDTH,
    spill.left + bandWidth + spill.right - 2 * CARD_EDGE_GAP,
  )

  // Measured before paint, otherwise the card is placed from stale dimensions and visibly jumps.
  useLayoutEffect(() => {
    const element = cardRef.current
    if (!element) return
    const { width, height } = element.getBoundingClientRect()
    setBox((previous) =>
      previous.width === width && previous.height === height ? previous : { width, height },
    )
  }, [anchor, title, rows])

  const placement = placeCard(anchor, box.width, box.height, bandWidth, spill, chartHeight)

  return (
    // Above the veil: a card spills over later bands, whose veils would otherwise paint over it.
    <div
      className="pointer-events-none absolute z-20"
      style={{ left: placement.left, top: placement.top + topOffset, filter: CARD_SHADOW }}
    >
      <CardArrow side={placement.arrowSide} offset={placement.arrowOffset} />
      <CardBody
        ref={cardRef}
        minWidth={Math.min(CARD_MIN_WIDTH, maxWidth)}
        maxWidth={maxWidth}
        title={title}
        rows={rows}
      />
    </div>
  )
}

type DataKeyAccessor = (record: TimelineRecord) => number | null

interface BandPanelProps {
  band: TimelineBand
  records: TimelineRecord[]
  markers: TimelineMarker[]
  segments: TimelineBarSegment[]
  nowLine: string | null
  series: TimelineSeries[]
  dataKeyAccessors: Map<string, DataKeyAccessor>
  hasBarSeries: boolean
  projection: YProjection
  minSlotWidth: number
  slotWidth: number
  barGap: number
  bandGradientFrom?: string
  bandGradientTo?: string
  dimVeilColor?: string
  bandRadius: number
  headerTextColor?: string
  axisTickColor?: string
  tickFilter?: (t: string) => boolean
  tickFormatter?: (t: string) => string
  slotCard?: (t: string, values: Record<string, number | null>) => TimelineCardContent
  headerHeight: number
  stackHeader: boolean
  spill: CardSpill
  dimmedBandOpacity: number
  highlightBandColor?: string
  highlightTextColor?: string
}

function bandHeaderLabelStyle(
  highlighted: boolean | undefined,
  highlightBandColor: string | undefined,
  highlightTextColor: string | undefined,
  headerTextColor: string | undefined,
): CSSProperties | undefined {
  if (highlighted) {
    return { backgroundColor: highlightBandColor, color: highlightTextColor ?? headerTextColor }
  }
  if (headerTextColor) return { color: headerTextColor }
  return undefined
}

const BandPanel = ({
  band,
  records,
  markers,
  segments,
  nowLine,
  series,
  dataKeyAccessors,
  hasBarSeries,
  projection,
  minSlotWidth,
  slotWidth,
  barGap,
  bandGradientFrom,
  bandGradientTo,
  dimVeilColor,
  bandRadius,
  headerTextColor,
  axisTickColor,
  tickFilter,
  tickFormatter,
  slotCard,
  headerHeight,
  stackHeader,
  spill,
  dimmedBandOpacity,
  highlightBandColor,
  highlightTextColor,
}: BandPanelProps) => {
  const chartHeight = useChartHeight()
  const plotHeight = plotHeightOf(chartHeight)
  // Width follows the fitted slot so bars keep their gap whatever the window size.
  const bandWidth = Math.max(records.length, 1) * slotWidth
  const barWidthRatio = series.find((currentSeries) => currentSeries.type === 'bar')?.barWidthRatio
  const veilColor = dimVeilColor ?? bandGradientTo
  // Outside the chart SVG so the veil covers the band header too.
  const dimLeft = band.dimFrom ? instantRatio(band.dimFrom, band, records) * bandWidth : null
  const ticks = useMemo(
    () => (tickFilter ? thinTicks(records, tickFilter, slotWidth) : undefined),
    [records, tickFilter, slotWidth],
  )
  const tickInterval = ticks ? 0 : Math.max(0, Math.ceil(MIN_TICK_GAP_PX / slotWidth) - 1)
  const [hoveredMarker, setHoveredMarker] = useState<HoveredMarker | null>(null)
  const [hoveredSlot, setHoveredSlot] = useState<HoveredSlot | null>(null)
  const [headerHovered, setHeaderHovered] = useState(false)
  // The pointer moves continuously, so the band only re-renders when the slot itself changes.
  const hoveredSlotKey = useRef<string | null>(null)
  const handleSlotHover = (hovered: HoveredSlot | null): void => {
    const key = hovered?.t ?? null
    if (hoveredSlotKey.current === key) return
    hoveredSlotKey.current = key
    setHoveredSlot(hovered)
  }
  const slotCardContent =
    hoveredSlot && slotCard ? slotCard(hoveredSlot.t, hoveredSlot.values) : null
  // The only unstable prop XAxis gets: recharts memoises it shallowly, so a fresh object rebuilds
  // the whole axis pipeline on every hover.
  const tickStyle = useMemo(
    () => ({
      dx: -slotWidth / 2,
      fontSize: 10,
      ...(axisTickColor ? { fill: axisTickColor } : {}),
    }),
    [slotWidth, axisTickColor],
  )

  const headerLabelStyle = bandHeaderLabelStyle(
    band.highlighted,
    highlightBandColor,
    highlightTextColor,
    headerTextColor,
  )

  return (
    <div className="relative flex shrink-0 flex-col" style={{ width: bandWidth }}>
      <div
        className="relative flex flex-col overflow-hidden"
        style={{
          opacity: band.dimmed ? dimmedBandOpacity : undefined,
          borderRadius: `${bandRadius}px ${bandRadius}px 0 0`,
          background:
            bandGradientFrom && bandGradientTo
              ? `linear-gradient(to bottom, ${bandGradientFrom}, ${bandGradientTo})`
              : undefined,
        }}
      >
        {dimLeft === null ? null : (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10"
            style={{ left: dimLeft, backgroundColor: veilColor, opacity: DIM_OPACITY }}
          />
        )}

        <div
          className="flex flex-col justify-center px-1"
          style={{ height: headerHeight }}
          onMouseEnter={() => setHeaderHovered(true)}
          onMouseLeave={() => setHeaderHovered(false)}
        >
          <div className="relative flex items-center justify-end">
            <span
              className={cn(
                'absolute inset-x-0 mx-auto w-fit text-center text-xs font-semibold uppercase',
                band.highlighted && 'rounded-full px-3 py-0.5',
                !headerTextColor && !band.highlighted && 'text-muted-foreground',
              )}
              style={headerLabelStyle}
            >
              {band.label}
            </span>
            {stackHeader ? null : band.header}
          </div>
          {stackHeader ? <div className="flex justify-center">{band.header}</div> : null}
        </div>
        <div className="relative [&_*:focus]:outline-none" style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={records}
              margin={CHART_MARGIN}
              barGap={barGap}
              // Its tabIndex hands the chart a focus ring for a keyboard mode it has not got.
              accessibilityLayer={false}
            >
              {/* The lowest tick is the plot floor: a line there would sit on the axis itself. */}
              <CartesianGrid
                vertical={false}
                horizontalPoints={projection.ticks
                  .slice(1)
                  .map((tick) => PLOT_TOP + plotHeight * (1 - projection.ratio(tick)))}
                stroke={GRID_LINE_COLOR}
                strokeOpacity={GRID_LINE_OPACITY}
                strokeDasharray={GRID_DASH}
              />
              <XAxis
                dataKey="t"
                orientation="top"
                height={X_AXIS_HEIGHT}
                tickFormatter={tickFormatter}
                ticks={ticks}
                interval={tickInterval}
                tickLine={false}
                axisLine={false}
                tickMargin={26}
                tick={tickStyle}
              />
              {/* allowDataOverflow : sans donnée (bande vide), recharts ignore sinon le domaine fourni. */}
              <YAxis width={0} hide domain={PROJECTED_DOMAIN} allowDataOverflow />
              {series.map((currentSeries) =>
                currentSeries.type === 'bar' ? (
                  <Bar
                    key={currentSeries.key}
                    dataKey={dataKeyAccessors.get(currentSeries.key)}
                    name={currentSeries.label}
                    fill={currentSeries.color}
                    fillOpacity={currentSeries.opacity ?? 1}
                    radius={currentSeries.radius ?? 0}
                    barSize={barSizeFor(
                      minSlotWidth,
                      currentSeries.barWidthRatio ?? DEFAULT_BAR_WIDTH_RATIO,
                    )}
                    isAnimationActive={false}
                  >
                    {currentSeries.highlightColor && hoveredSlot
                      ? records.map((record) => (
                          <Cell
                            key={record.t}
                            fill={
                              record.t === hoveredSlot.t
                                ? currentSeries.highlightColor
                                : currentSeries.color
                            }
                            fillOpacity={
                              record.t === hoveredSlot.t ? 1 : (currentSeries.opacity ?? 1)
                            }
                          />
                        ))
                      : null}
                  </Bar>
                ) : (
                  <Line
                    key={currentSeries.key}
                    dataKey={dataKeyAccessors.get(currentSeries.key)}
                    name={currentSeries.label}
                    stroke={currentSeries.color}
                    strokeOpacity={currentSeries.opacity ?? 1}
                    strokeWidth={currentSeries.strokeWidth ?? LINE_STROKE_WIDTH}
                    strokeDasharray={currentSeries.dashed ? '4 2' : undefined}
                    dot={false}
                    type="monotone"
                    isAnimationActive={false}
                  />
                ),
              )}
              <Customized<BandOverlayProps, typeof BandOverlay>
                component={BandOverlay}
                band={band}
                records={records}
                markers={markers}
                segments={segments}
                projection={projection}
                nowLine={nowLine}
                hasBarSeries={hasBarSeries}
                barWidthRatio={barWidthRatio}
                onSlotHover={handleSlotHover}
                onMarkerHover={setHoveredMarker}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Outside the clipped band: the card is wider than a nine-day band. */}
      {hoveredMarker ? (
        <HoverCard
          anchor={{ x: hoveredMarker.x, y: hoveredMarker.y, clearance: MARKER_CLEARANCE }}
          bandWidth={bandWidth}
          spill={spill}
          topOffset={headerHeight}
          title={hoveredMarker.marker.title}
          rows={hoveredMarker.marker.details ?? []}
        />
      ) : null}
      {!hoveredMarker && hoveredSlot && slotCardContent ? (
        <HoverCard
          anchor={{
            x: hoveredSlot.x,
            y: hoveredSlot.y,
            clearance: slotWidth / 2 + CARD_ARROW_DEPTH,
          }}
          bandWidth={bandWidth}
          spill={spill}
          topOffset={headerHeight}
          title={slotCardContent.title}
          rows={slotCardContent.rows}
        />
      ) : null}
      {headerHovered && band.headerCard ? (
        <div
          className="pointer-events-none absolute inset-x-0 z-20 flex justify-center"
          style={{ top: headerHeight + CARD_ARROW_DEPTH, filter: CARD_SHADOW }}
        >
          <CardArrow side="top" offset="50%" />
          <CardBody
            minWidth={CARD_MIN_WIDTH}
            maxWidth={CARD_MAX_WIDTH}
            title={band.headerCard.title}
            rows={band.headerCard.rows}
          />
        </div>
      ) : null}
    </div>
  )
}

// No column of its own: the labels float over the left edge of the plot, on the same rows as
// the bands' plot area, which the pinned header height and X axis height make computable.
const YAxisOverlay = ({
  projection,
  axisTickColor,
  headerHeight,
  markerRowBottom,
}: {
  projection: YProjection
  axisTickColor?: string
  headerHeight: number
  markerRowBottom: number
}) => {
  const plotHeight = usePlotHeight()
  return (
    <div
      className="pointer-events-none absolute"
      style={{ left: Y_AXIS_LABEL_INSET, top: headerHeight + PLOT_TOP, height: plotHeight }}
    >
      {projection.ticks
        .map((tick) => ({ tick, top: plotHeight * (1 - projection.ratio(tick)) }))
        .filter(({ top }) => top >= markerRowBottom)
        .map(({ tick, top }) => (
          <span
            key={tick}
            className={cn(
              'absolute left-0 -translate-y-1/2 text-[10px] font-normal leading-none',
              !axisTickColor && 'text-muted-foreground',
            )}
            style={{
              top: Math.min(top, plotHeight - AXIS_LABEL_HALF_HEIGHT),
              ...(axisTickColor ? { color: axisTickColor } : {}),
            }}
          >
            {formatNumber(tick)}
          </span>
        ))}
    </div>
  )
}

export const BandedTimelineChart = ({
  series,
  markers,
  barSegments = [],
  bands,
  nowLine,
  yScaleMidpoint,
  minSlotWidth = DEFAULT_MIN_SLOT_WIDTH,
  slotCard,
  bandGradientFrom,
  bandGradientTo,
  bandRadius = 0,
  dimVeilColor,
  headerTextColor,
  axisTickColor,
  barLayout = 'side-by-side',
  tickFilter,
  tickFormatter,
  dimmedBandOpacity = 0.5,
  highlightBandColor,
  highlightTextColor,
  height = DEFAULT_CHART_HEIGHT,
  fitAllBands = false,
}: BandedTimelineChartProps) => {
  const records = useMemo(() => buildRecords(series), [series])
  const projection = useMemo(
    () => createProjection(computeYDomain(series), yScaleMidpoint, plotHeightOf(height)),
    [series, yScaleMidpoint, height],
  )
  const hasBarSeries = useMemo(
    () => series.some((currentSeries) => currentSeries.type === 'bar'),
    [series],
  )
  const [scrollerRef, scrollerWidth] = useMeasuredWidth<HTMLDivElement>()
  // Records carry their epoch, so the bands are partitioned once in a single pass.
  const bandRecords = useMemo(
    () =>
      bands.map((band) => {
        const [from, to] = bandBounds(band)
        return records.filter((record) => record.epoch >= from && record.epoch < to)
      }),
    [bands, records],
  )
  // Bands share the full row: the slot follows the available width so bars never drift apart.
  // With a now line, one band's worth stays off screen, so the view can scroll half a band either
  // side to centre that instant, over real data rather than empty padding.
  const fittedSlotWidth = useMemo(() => {
    const slots = bandRecords.reduce((total, slice) => total + slice.length, 0)
    if (scrollerWidth === 0 || slots === 0) return minSlotWidth
    const widestBand = bandRecords.reduce((max, slice) => Math.max(max, slice.length), 0)
    const visible = nowLine && !fitAllBands && slots > widestBand ? slots - widestBand : slots
    const available = scrollerWidth - Math.max(0, bands.length - 1) * BAND_GAP_PX
    return Math.max(SLOT_WIDTH_FLOOR, Math.floor((available / visible) * 100) / 100)
  }, [bandRecords, bands.length, scrollerWidth, minSlotWidth, nowLine, fitAllBands])
  const stackHeader = useMemo(() => {
    const widest =
      bandRecords.reduce((max, slice) => Math.max(max, slice.length), 0) * fittedSlotWidth
    return widest > 0 && widest < HEADER_EXTRAS_MIN_WIDTH
  }, [bandRecords, fittedSlotWidth])
  const headerHeight = stackHeader ? HEADER_ROW_HEIGHT + HEADER_BADGE_ROW_HEIGHT : HEADER_ROW_HEIGHT
  const barGap = useMemo(
    () => computeBarGap(series, fittedSlotWidth, barLayout),
    [series, fittedSlotWidth, barLayout],
  )
  const slotWidth = useMemo(
    () => computeSlotWidth(series, fittedSlotWidth, barGap),
    [series, fittedSlotWidth, barGap],
  )
  // Direct key access rather than a dotted path, so a key holding a dot or bracket stays safe.
  // Values reach recharts already projected: its own axis only ever maps a domain linearly.
  const dataKeyAccessors = useMemo(
    () =>
      new Map<string, DataKeyAccessor>(
        series.map((currentSeries) => [
          currentSeries.key,
          (record: TimelineRecord) => {
            const value = record.values[currentSeries.key]
            return value === null ? null : projection.ratio(value)
          },
        ]),
      ),
    [series, projection],
  )
  // Stable references across renders: onHover must not rebuild records and markers every call.
  const bandData = useMemo(() => {
    let offset = 0
    const entries = bands.map((band, index) => {
      const slice = bandRecords[index]
      const width = Math.max(slice.length, 1) * slotWidth
      const entry = {
        band,
        records: slice,
        markers: markers.filter((marker) => isWithinBand(marker.t, band)),
        segments: barSegments.filter((segment) => isWithinBand(segment.t, band)),
        nowLine: nowLine && isWithinBand(nowLine, band) ? nowLine : null,
        offset,
        width,
      }
      offset += width + BAND_GAP_PX
      return entry
    })
    const total = Math.max(0, offset - BAND_GAP_PX)
    return entries.map((entry) => ({
      ...entry,
      spill: { left: entry.offset, right: total - entry.offset - entry.width },
    }))
  }, [bands, bandRecords, markers, barSegments, nowLine, slotWidth])

  const nowCentreX = useMemo(() => {
    if (!nowLine) return null
    const entry = bandData.find((candidate) => candidate.nowLine !== null)
    if (!entry) return null
    return entry.offset + instantRatio(nowLine, entry.band, entry.records) * entry.width
  }, [bandData, nowLine])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || nowCentreX === null) return
    scroller.scrollLeft = nowCentreX - scroller.clientWidth / 2
  }, [nowCentreX, scrollerRef])

  return (
    <ChartHeightContext value={height}>
      <div className="relative">
        <div
          ref={scrollerRef}
          className={cn(
            'flex overflow-y-hidden',
            // fitAllBands (store) shows every band at once: no scroll, so clip to avoid a
            // transient horizontal scrollbar on hover. Otherwise scroll to centre the now line.
            fitAllBands ? 'overflow-x-hidden' : 'overflow-x-auto',
          )}
          style={{ gap: BAND_GAP_PX, height: headerHeight + height }}
        >
          {bandData.map(
            ({
              band,
              records: bandRecords,
              markers: bandMarkers,
              segments: bandSegments,
              nowLine: bandNowLine,
              spill,
            }) => (
              <BandPanel
                key={band.key}
                band={band}
                records={bandRecords}
                markers={bandMarkers}
                segments={bandSegments}
                nowLine={bandNowLine}
                spill={spill}
                series={series}
                dataKeyAccessors={dataKeyAccessors}
                hasBarSeries={hasBarSeries}
                projection={projection}
                minSlotWidth={fittedSlotWidth}
                slotWidth={slotWidth}
                barGap={barGap}
                bandGradientFrom={bandGradientFrom}
                bandGradientTo={bandGradientTo}
                dimVeilColor={dimVeilColor}
                bandRadius={bandRadius}
                headerTextColor={headerTextColor}
                axisTickColor={axisTickColor}
                headerHeight={headerHeight}
                stackHeader={stackHeader}
                tickFilter={tickFilter}
                tickFormatter={tickFormatter}
                slotCard={slotCard}
                dimmedBandOpacity={dimmedBandOpacity}
                highlightBandColor={highlightBandColor}
                highlightTextColor={highlightTextColor}
              />
            ),
          )}
        </div>
        <YAxisOverlay
          markerRowBottom={
            markers.some((marker) => marker.dropLine)
              ? DROP_LINE_MARKER_OFFSET + MARKER_SLOT_SIZE / 2
              : 0
          }
          projection={projection}
          axisTickColor={axisTickColor}
          headerHeight={headerHeight}
        />
      </div>
    </ChartHeightContext>
  )
}
